import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Clock, 
  Repeat, 
  DollarSign, 
  Zap, 
  Crown, 
  Ticket, 
  MousePointer2, 
  UserPlus, 
  RotateCcw,
  Sparkles,
  ArrowRight,
  MessageSquare,
  ShoppingCart,
  Activity,
  Send,
  Users
} from 'lucide-react';
import '../segments/Segments.css';

const Segments = () => {
  const [selectedSegment, setSelectedSegment] = useState('Ready-to-Buy');

  const behaviorSegments = [
    { 
      id: 'Ready-to-Buy', 
      icon: Zap, 
      label: "High Intent", 
      title: "Ready-to-Buy", 
      subtitle: "Added to cart > 2x in 24h", 
      value: "842", 
      trend: "+12%" 
    },
    { 
      id: 'VIPs', 
      icon: Crown, 
      title: "VIPs", 
      subtitle: "LTV > $500 & 3+ Orders", 
      value: "1,204", 
      trend: "+4%" 
    },
    { 
      id: 'New Leads', 
      icon: UserPlus, 
      title: "New Leads", 
      subtitle: "Joined < 7 days", 
      value: "890", 
      trend: "+24%" 
    },
    { 
      id: 'Returning', 
      icon: RotateCcw, 
      title: "Returning", 
      subtitle: "Back after 30d absence", 
      value: "412", 
      trend: "+3%" 
    },
    { 
      id: 'Coupon Seekers', 
      icon: Ticket, 
      title: "Coupon Seekers", 
      subtitle: "Abandon cart wait > 1d", 
      value: "3,450", 
      trend: "-3%" 
    },
    { 
      id: 'Price inquiry', 
      icon: MessageSquare, 
      title: "Price inquiry", 
      subtitle: "Asked about pricing/discounts", 
      value: "1,520", 
      trend: "+18%" 
    },
    { 
      id: 'CartAbandoners', 
      icon: ShoppingCart, 
      title: "CartAbandoners", 
      subtitle: "Items left in cart > 24h", 
      value: "2,410", 
      trend: "+11%" 
    },
    { 
      id: 'Browsers', 
      icon: MousePointer2, 
      title: "Browsers", 
      subtitle: "3+ sessions, 0 cart", 
      value: "5,102", 
      trend: "— 0%" 
    }
  ];

  const rfmData = [
    { 
      title: "RECENCY", 
      value: "12.4", 
      unit: "days avg", 
      icon: Clock, 
      segments: [
        { label: 'Active', percent: 45, color: 'emerald' }, 
        { label: 'At Risk', percent: 22, color: 'gray' }
      ] 
    },
    { 
      title: "FREQUENCY", 
      value: "3.8", 
      unit: "orders/yr", 
      icon: Repeat, 
      segments: [
        { label: 'Loyal', percent: 18, color: 'purple' }, 
        { label: 'One-time', percent: 54, color: 'blue' }
      ] 
    },
    { 
      title: "MONETARY (LTV)", 
      value: "$342", 
      unit: "avg/user", 
      icon: DollarSign, 
      segments: [
        { label: 'Whales', percent: 8, color: 'orange' }, 
        { label: 'Mid-Tier', percent: 35, color: 'orange-dark' }
      ] 
    }
  ];

  const currentSegmentData = behaviorSegments.find(s => s.id === selectedSegment) || behaviorSegments[0];

  return (
    <>
      <Helmet>
        <title>Segmentation Hub - Revexia AI Revenue Brain</title>
        <meta name="description" content="Smart behavior segments and RFM analysis" />
      </Helmet>

      <div className="segments-page-wrapper">
        <div className="container-fluid px-4">
          <div className="row g-4">
            {/* Main Content Area */}
            <div className="col-12 col-xl-8">
              <div className="segments-main-content">
                {/* RFM Analysis Section */}
                <section className="segments-section">
                  <div className="section-header-with-icon">
                    <Activity size={20} className="section-icon emerald" />
                    <h2 className="section-main-title">RFM Analysis</h2>
                  </div>
                  <div className="row g-4">
                    {rfmData.map((rfm, index) => (
                      <div key={index} className="col-12 col-md-4">
                        <div className="rfm-card">
                          <div className="rfm-card-header">
                            <div className="rfm-card-info">
                              <p className="rfm-card-label">{rfm.title}</p>
                              <div className="rfm-card-value-wrapper">
                                <span className="rfm-card-value">{rfm.value}</span>
                                <span className="rfm-card-unit">{rfm.unit}</span>
                              </div>
                            </div>
                            <div className="rfm-card-icon">
                              <rfm.icon size={24} />
                            </div>
                          </div>
                          <div className="rfm-segments-list">
                            {rfm.segments.map((seg, i) => (
                              <div key={i} className="rfm-segment-item">
                                <div className="rfm-segment-header">
                                  <span className="rfm-segment-label">{seg.label}</span>
                                  <span className="rfm-segment-percent">{seg.percent}%</span>
                                </div>
                                <div className="rfm-segment-progress-track">
                                  <div 
                                    className={`rfm-segment-progress-bar progress-${seg.color}`}
                                    style={{ width: `${seg.percent}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Behavior Segments Section */}
                <section className="segments-section">
                  <div className="section-header-with-status">
                    <div className="section-header-with-icon">
                      <Users size={20} className="section-icon cyan" />
                      <h2 className="section-main-title">Smart Behavior Segments</h2>
                    </div>
                    <div className="live-updates-badge">
                      <span className="live-pulse-dot"></span>
                      <span className="live-text">Live Updates</span>
                    </div>
                  </div>
                  <div className="row g-4">
                    {behaviorSegments.map((seg) => (
                      <div key={seg.id} className="col-12 col-md-6 col-lg-4">
                        <div 
                          onClick={() => setSelectedSegment(seg.id)}
                          className={`behavior-segment-card ${selectedSegment === seg.id ? 'active' : ''}`}
                        >
                          <div className="behavior-card-header">
                            <div className="behavior-card-icon">
                              <seg.icon size={20} />
                            </div>
                            {seg.label && (
                              <span className="behavior-card-badge">{seg.label}</span>
                            )}
                          </div>
                          <div className="behavior-card-body">
                            <h4 className="behavior-card-title">{seg.title}</h4>
                            <p className="behavior-card-subtitle">{seg.subtitle}</p>
                            <div className="behavior-card-footer">
                              <span className="behavior-card-value">{seg.value}</span>
                              {seg.trend && (
                                <span className={`behavior-card-trend ${
                                  seg.trend.startsWith('+') ? 'positive' : 
                                  seg.trend === '0%' || seg.trend === '— 0%' ? 'neutral' : 
                                  'negative'
                                }`}>
                                  {seg.trend}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Logic Builder Section */}
                <section className="segments-section">
                  <div className="section-header-with-icon">
                    <Sparkles size={20} className="section-icon purple" />
                    <h2 className="section-main-title">Logic Builder</h2>
                  </div>
                  <div className="logic-builder-card">
                    <div className="logic-builder-rule">
                      <span className="logic-operator">IF</span>
                      <div className="logic-condition">
                        <MessageSquare size={16} />
                        <span>Interaction contains "shipping"</span>
                      </div>
                    </div>
                    <div className="logic-builder-rule">
                      <span className="logic-operator">AND</span>
                      <div className="logic-condition">
                        <ShoppingCart size={16} />
                        <span>Cart Value &gt; $50.00</span>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* Right Context Sidebar */}
            <div className="col-12 col-xl-4">
              <div className="context-sidebar">
                {/* Segment Header */}
                <div className="context-sidebar-header">
                  <div className="context-icon-wrapper">
                    <currentSegmentData.icon size={28} />
                  </div>
                  <div className="context-header-info">
                    <p className="context-header-label">Context Analysis</p>
                    <h3 className="context-header-title">{currentSegmentData.title}</h3>
                  </div>
                </div>

                {/* Population Stats */}
                <div className="context-stats-section">
                  <div className="context-population">
                    <div className="population-info">
                      <p className="population-label">Population</p>
                      <p className="population-value">{currentSegmentData.value}</p>
                    </div>
                    <div className="population-chart">
                      {[30, 45, 25, 60, 40, 75, 50].map((h, i) => (
                        <div 
                          key={i} 
                          className={`population-bar ${i === 5 ? 'active' : ''}`}
                          style={{ height: `${h}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="context-metric-card">
                        <p className="context-metric-label">CTR</p>
                        <p className="context-metric-value emerald">12.8%</p>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="context-metric-card">
                        <p className="context-metric-label">AOV</p>
                        <p className="context-metric-value">$68</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategic Next Steps */}
                <div className="context-actions-section">
                  <h4 className="context-section-title">Strategic Next Steps</h4>
                  <div className="context-actions-list">
                    {[
                      { title: 'Send Personalized Follow-up', tag: 'Top Pick', color: 'cyan' },
                      { title: 'Trigger Smart Discount', tag: 'Safe', color: 'gray' }
                    ].map((step, i) => (
                      <div key={i} className={`context-action-card action-${step.color}`}>
                        <div className="context-action-header">
                          <span className="context-action-tag">{step.tag}</span>
                          <ArrowRight size={14} className="context-action-arrow" />
                        </div>
                        <p className="context-action-title">{step.title}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Engage Button */}
                <div className="context-engage-section">
                  <button className="engage-segment-btn">
                    <Send size={18} />
                    <span>Engage Segment</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Segments;
